import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import ScrollProgressBar from '../common/ScrollProgressBar';

const TABLET_VIEW_MODE_STORAGE_KEY = 'tablet-view-mode';
const DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY = 'desktop-sidebar-collapsed';
const DESKTOP_VIEWPORT_QUERY = '(min-width: 1024px)';

const getIsDesktopViewport = () => {
  if (typeof window === 'undefined') return true;
  return window.matchMedia(DESKTOP_VIEWPORT_QUERY).matches;
};

const getStoredDesktopSidebarCollapsed = () => {
  if (typeof window === 'undefined') return true;
  const saved = window.localStorage.getItem(DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY);
  if (saved === 'false') return false;
  if (saved === 'true') return true;
  return true;
};

const detectDeviceMode = () => {
  if (typeof window === 'undefined') return 'desktop';

  const ua = String(window.navigator.userAgent || '').toLowerCase();
  const isIpad = /ipad/.test(ua)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

  const isTablet = isIpad
    || /tablet|playbook|silk/.test(ua)
    || (/android/.test(ua) && !/mobile/.test(ua));
  const isMobile = /mobile|iphone|ipod|android.*mobile|windows phone/.test(ua);

  if (isTablet) return 'tablet';
  if (isMobile) return 'mobile';
  return 'desktop';
};

const MainLayout = () => {
  const location = useLocation();
  const mainScrollRef = useRef(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(() => getStoredDesktopSidebarCollapsed());
  const [deviceMode] = useState(() => detectDeviceMode());
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => getIsDesktopViewport());
  const [tabletViewMode, setTabletViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'web';
    const saved = window.localStorage.getItem(TABLET_VIEW_MODE_STORAGE_KEY);
    return saved === 'mobile' ? 'mobile' : 'web';
  });

  const isTabletDevice = deviceMode === 'tablet';
  const isWebMode = isDesktopViewport;
  const usesDesktopSidebar = isDesktopViewport;

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(DESKTOP_VIEWPORT_QUERY);
    const syncViewportMode = () => setIsDesktopViewport(mediaQuery.matches);
    syncViewportMode();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewportMode);
      return () => mediaQuery.removeEventListener('change', syncViewportMode);
    }

    mediaQuery.addListener(syncViewportMode);
    return () => mediaQuery.removeListener(syncViewportMode);
  }, []);

  useEffect(() => {
    if (!isMobileSidebarOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TABLET_VIEW_MODE_STORAGE_KEY, tabletViewMode);
  }, [tabletViewMode]);

  useEffect(() => {
    if (usesDesktopSidebar) {
      setIsMobileSidebarOpen(false);
      setIsDesktopSidebarCollapsed(getStoredDesktopSidebarCollapsed());
    } else {
      setIsDesktopSidebarCollapsed(true);
    }
  }, [usesDesktopSidebar]);

  const handleToggleSidebar = () => {
    if (usesDesktopSidebar) {
      setIsDesktopSidebarCollapsed((current) => {
        const nextValue = !current;
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY, String(nextValue));
        }
        return nextValue;
      });
      return;
    }

    setIsMobileSidebarOpen((current) => !current);
  };

  return (
    <div className="h-screen bg-gray-50 overflow-hidden">
      <div className={`flex h-screen bg-gray-50 ${usesDesktopSidebar ? (isDesktopSidebarCollapsed ? 'pl-20' : 'pl-64') : ''}`}>
        <Sidebar
          isWebMode={usesDesktopSidebar}
          isMobileOpen={isMobileSidebarOpen}
          isDesktopCollapsed={isDesktopSidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
          onCloseMobile={() => {
            setIsMobileSidebarOpen(false);
            setIsDesktopSidebarCollapsed(true);
          }}
        />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <ScrollProgressBar scrollRef={mainScrollRef} watchKey={location.pathname} />
          <Header
            onToggleSidebar={handleToggleSidebar}
            isWebMode={usesDesktopSidebar}
            isTabletViewport={isTabletDevice}
            tabletViewMode={tabletViewMode}
            onChangeTabletViewMode={setTabletViewMode}
            isMobileSidebarOpen={isMobileSidebarOpen}
            isDesktopSidebarCollapsed={isDesktopSidebarCollapsed}
          />
          <main ref={mainScrollRef} className={`app-scrollbar flex-1 overflow-x-auto overflow-y-auto bg-gray-50 ${isWebMode ? 'p-6' : 'p-4'}`}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
